import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab
} from '@mui/material';
import {
  Download,
  People,
  Assessment,
  Quiz,
  CreditCard,
  Score,
  TrendingUp,
  FilterList
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import DashboardSwitcher from '../components/DashboardSwitcher';
import QuestionManager from '../components/QuestionManager';
import SubscriptionManager from '../components/SubscriptionManager';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5006/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [filters, setFilters] = useState({
    status: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('adminToken');

      if (!token) {
        navigate('/auth');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };

      // Fetch all three data sources in parallel
      const [statsResponse, participantsResponse, testsResponse] = await Promise.all([
        axios.get(`${API_URL}/admin/dashboard/stats`, { headers }),
        axios.get(`${API_URL}/admin/participants`, { headers }),
        axios.get(`${API_URL}/admin/tests`, { headers, params: filters })
      ]);

      setStats(statsResponse.data);
      setParticipants(participantsResponse.data);
      setTests(testsResponse.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/auth');
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to load dashboard data. Is the backend running?');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
  try {
    const token = localStorage.getItem('adminToken');
    
    // Create a hidden link element
    const response = await fetch(`${API_URL}/admin/export/excel`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Export failed');
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `assessment_results_${new Date().toISOString().split('T')[0]}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    toast.success('Excel file downloaded successfully!');
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    toast.error('Failed to export data. Please try again.');
  }
};

const handleExportPDF = async (testId) => {
  try {
    const token = localStorage.getItem('adminToken');
    
    const response = await fetch(`${API_URL}/admin/export/pdf/${testId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('PDF generation failed');
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `test_report_${testId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    toast.success('PDF report downloaded successfully!');
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    toast.error('Failed to generate PDF. Please try again.');
  }
};

const handleExportParticipants = async () => {
  try {
    const token = localStorage.getItem('adminToken');
    
    const response = await fetch(`${API_URL}/admin/export/participants`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Export failed');
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `participants_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    toast.success('Participants list downloaded successfully!');
  } catch (error) {
    console.error('Error exporting participants:', error);
    toast.error('Failed to export participants. Please try again.');
  }
};


<Button
  variant="outlined"
  startIcon={<Download />}
  onClick={handleExportParticipants}
  sx={{ mr: 1 }}
>
  Export Participants
</Button>

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    fetchDashboardData();
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      startDate: '',
      endDate: ''
    });
    fetchDashboardData();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'warning';
      case 'expired': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading dashboard...</Typography>
      </Container>
    );
  }

  return (
    <>
      <DashboardSwitcher title="Admin Panel" />
      <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
        {/* Header */}
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" gutterBottom>
                Admin Dashboard
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                System-wide statistics and management
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              {activeTab === 0 && (
                <Button variant="outlined" startIcon={<Download />} onClick={handleExportExcel}>
                  Export Excel
                </Button>
              )}
            </Box>
          </Box>

          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mt: 2 }}>
            <Tab icon={<Assessment />} label="Dashboard" iconPosition="start" />
            <Tab icon={<Quiz />} label="Question Bank" iconPosition="start" />
            <Tab icon={<CreditCard />} label="Subscription" iconPosition="start" />
          </Tabs>
        </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <>
      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <People sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4">{stats?.totalParticipants || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Participants</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Assessment sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4">{stats?.totalTests || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Tests</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Score sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4">{stats?.completedTests || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Completed Tests</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4">{stats?.averageScore?.toFixed(1) || '0.0'}%</Typography>
                  <Typography variant="body2" color="text.secondary">Average Score</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Summary */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Performance by Category
        </Typography>
        <Grid container spacing={2}>
          {stats?.categoryStats?.map((categoryStat) => {
            const percentage = parseFloat(categoryStat.percentage);
            return (
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={categoryStat.category}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      {categoryStat.category.replace('_', ' ')}
                    </Typography>
                    <Typography variant="h5" gutterBottom>
                      {percentage}%
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={percentage}
                      color={
                        percentage >= 80 ? 'success' : 
                        percentage >= 60 ? 'primary' : 
                        'warning'
                      }
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {categoryStat.correctAnswers}/{categoryStat.totalQuestions} correct
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Filters */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterList sx={{ mr: 1 }} />
          <Typography variant="h6">Filters</Typography>
        </Box>
        
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={applyFilters}>
            Apply Filters
          </Button>
          <Button variant="outlined" onClick={clearFilters}>
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {/* Tests Table */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Recent Tests ({tests.length})
        </Typography>
        
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Participant</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tests.map((test) => (
                <TableRow key={test.id} hover>
                  <TableCell>{test.full_name || 'N/A'}</TableCell>
                  <TableCell>{test.email}</TableCell>
                  <TableCell>{formatDate(test.start_time)}</TableCell>
                  <TableCell>
                    {test.duration ? `${Math.floor(test.duration / 60)}m ${test.duration % 60}s` : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {test.score !== null ? (
                      <Chip 
                        label={`${parseFloat(test.score).toFixed(1)}%`}
                        color={test.score >= 80 ? 'success' : test.score >= 60 ? 'primary' : 'warning'}
                        size="small"
                      />
                    ) : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={test.status || 'unknown'}
                      color={getStatusColor(test.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      startIcon={<Download />}
                      onClick={() => handleExportPDF(test.id)}
                      disabled={test.status !== 'completed'}
                    >
                      PDF
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Participants Table */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Participants ({participants.length})
        </Typography>
        
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Institution</TableCell>
                <TableCell>Registration Date</TableCell>
                <TableCell>Last Test</TableCell>
                <TableCell>Tests Taken</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {participants.map((participant) => (
                <TableRow key={participant.id} hover>
                  <TableCell>{participant.full_name || 'N/A'}</TableCell>
                  <TableCell>{participant.email}</TableCell>
                  <TableCell>{participant.institution || 'N/A'}</TableCell>
                  <TableCell>{formatDate(participant.registration_date)}</TableCell>
                  <TableCell>{formatDate(participant.last_test_date)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={participant.total_tests_taken || 0}
                      color={participant.total_tests_taken > 0 ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
        </>
      )}

      {activeTab === 1 && (
        <QuestionManager />
      )}

      {activeTab === 2 && (
        <SubscriptionManager />
      )}
    </Container>
    </>
  );
};

export default AdminDashboard;